import {usePathname} from 'next/navigation';
import {useEffect, useRef} from 'react';

import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from '../../context/YandexAdsContext';
import {EmbedCodeBase} from '../../types';
import {getEventPayloadForAd} from '../../utils/getEventPayloadForAd';

type Props = {
  containerId: string;
  code: EmbedCodeBase;
};

export const useDivAvailabilityTracker = ({containerId, code}: Props) => {
  const addEvent = useYandexAdsContextSelector(
    YandexAdsContextCommonSelectors.getEmitAdFoxEvent,
  );
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const element = document.getElementById(containerId);
    if (!element) {
      return;
    }

    const cleanupCallbacks: VoidFunction[] = [];
    let visible = false;
    let filled = false;
    const sendState = () => {
      addEvent({
        eventName: 'AvailabilityDivForAds',
        payload: {
          ...getEventPayloadForAd({containerId, code}),
          was_visible: `${+visible}`,
          was_filled: `${+filled}`,
          pathname: pathnameRef.current,
        },
      });
    };

    window.addEventListener('beforeunload', sendState);
    cleanupCallbacks.push(() =>
      window.removeEventListener('beforeunload', sendState),
    );

    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          visible = true;
          observer.disconnect();
        }
      });
      observer.observe(element);
      cleanupCallbacks.push(() => observer.disconnect());
    }

    if (window.MutationObserver) {
      const observer = new MutationObserver(() => {
        filled = true;
        observer.disconnect();
      });
      observer.observe(element, {childList: true});
      cleanupCallbacks.push(() => observer.disconnect());
    }

    return () => {
      cleanupCallbacks.forEach((cb) => cb());
      sendState();
    };
  }, [containerId, code, addEvent]);
};
